import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { parivartanAPI } from '../../../services/parivartanAPI';
import { canEditTender } from '../../../utils/tenderRecordAccess';
import { useWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import './TenderSubmissionTab.scss';

const inrPaise = (paise, currency = 'INR') => {
  if (paise == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(Number(paise) / 100);
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const labelize = (v) =>
  String(v || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const visibleReadiness = (readiness, canReadCosts) => {
  if (!readiness || canReadCosts) return readiness || null;
  const estimate = readiness.approvedEstimate;
  return {
    ...readiness,
    approvedEstimate: estimate ? { version: estimate.version, approved: true } : null
  };
};

const TenderSubmissionTab = ({ tenderId, tender, onRecorded }) => {
  const { allowed, ready } = useWorkspaceAccess();
  const user = useSelector((state) => state.auth.user);
  const ownerId = tender?.owner?._id || tender?.owner;
  const isPreparer = ownerId && String(ownerId) === String(user?._id || user?.id || '');
  const canReadCosts = ready && allowed(['tender.costing.read']);
  const canApproveSubmission = ready && allowed(['tender.decision.approve']) && !isPreparer;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [portalReference, setPortalReference] = useState('');
  const [acknowledgementEvidence, setAcknowledgementEvidence] = useState('');
  const [checklist, setChecklist] = useState([]);
  const [clarificationQ, setClarificationQ] = useState('');
  const [responseDrafts, setResponseDrafts] = useState({});
  const [approval, setApproval] = useState(null);

  const editable = canEditTender(tender) && !snapshot?.isImmutable;
  const journey = tender?.lifecycle?.journey || tender?.workflowStage || '';
  const readyToSubmit = journey === 'ready_to_submit';

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return parivartanAPI
      .getSubmission(tenderId)
      .then((res) => {
        const data = res.data?.data || {};
        setSnapshot(data.snapshot || null);
        setApproval(data.approval || null);
        setReadiness(visibleReadiness(data.readiness, canReadCosts));
        if (data.snapshot) {
          setPortalReference(data.snapshot.portalReference || '');
          setAcknowledgementEvidence(data.snapshot.acknowledgementEvidence || '');
          setChecklist(data.snapshot.checklist || []);
        } else {
          setPortalReference('');
          setAcknowledgementEvidence('');
          setChecklist((data.readiness?.checklist || []).map((row) => ({ ...row })));
        }
      })
      .catch(() => setError('Unable to load submission desk'))
      .finally(() => setLoading(false));
  }, [tenderId, canReadCosts]);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'submission',
    noun: 'submission',
    plural: 'submissions',
    reload: () => {
      load();
      if (onRecorded) onRecorded();
    },
    label: () => 'Submission',
    note: 'The submission record is removed. The tender and its documents stay.'
  });

  const toggleCheck = (index) => {
    if (!editable) return;
    setChecklist((rows) =>
      rows.map((row, i) => (i === index ? { ...row, completed: !row.completed } : row))
    );
  };

  const record = async () => {
    if (!editable) return;
    setSaving(true);
    setError('');
    try {
      const res = await parivartanAPI.recordSubmission(tenderId, {
        portalReference: portalReference.trim(),
        acknowledgementEvidence: acknowledgementEvidence.trim(),
        checklist,
        pinnedCredentials: (readiness?.credentials || []).map((c) => ({
          credentialId: c.credentialId,
          version: c.version
        })),
        pinnedCvs: (readiness?.personnel || []).map((p) => ({
          personnelId: p.personnelId,
          cvVersion: p.cvVersion
        })),
        pinnedDocuments: (readiness?.documents || []).map((d) => ({
          documentId: d.documentId,
          versionLabel: d.versionLabel
        }))
      });
      setSnapshot(res.data?.data?.snapshot || null);
      setReadiness(visibleReadiness(res.data?.data?.readiness, canReadCosts) || readiness);
      if (onRecorded) onRecorded();
      await load();
    } catch (err) {
      const blockers = err.response?.data?.data?.blockers;
      setError(
        blockers?.length
          ? blockers.join(' ')
          : err.response?.data?.message || 'Unable to record submission'
      );
    } finally {
      setSaving(false);
    }
  };

  const addClarification = async () => {
    if (!clarificationQ.trim()) return;
    setSaving(true);
    setError('');
    try {
      await parivartanAPI.addSubmissionClarification(tenderId, { question: clarificationQ.trim() });
      setClarificationQ('');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to add clarification');
    } finally {
      setSaving(false);
    }
  };

  const respondClarification = async (index) => {
    const draft = responseDrafts[index] || {};
    if (!draft.response?.trim()) return;
    setSaving(true);
    setError('');
    try {
      await parivartanAPI.respondSubmissionClarification(tenderId, index, {
        response: draft.response.trim(),
        outcome: (draft.outcome || '').trim()
      });
      setResponseDrafts((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to record clarification response');
    } finally {
      setSaving(false);
    }
  };

  const blockers = readiness?.blockers || [];
  const checklistDone = useMemo(
    () => checklist.filter((c) => c.completed).length,
    [checklist]
  );

  if (loading && !readiness) {
    return (
      <div className="tsub tsub--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (snapshot?.isImmutable) {
    return (
      <div className="tsub">
        {error ? <Alert variant="warning">{error}</Alert> : null}
        {admin.notice}
        <div className="tsub__banner">
          <div>
            <h3>Submission recorded</h3>
            <p>
              Portal {snapshot.portalReference || '—'} · {fmtDate(snapshot.submittedAt)} · commercial v
              {snapshot.commercialEstimateVersion || '—'} · technical v
              {snapshot.technicalResponseVersion || '—'}
            </p>
          </div>
          <div className="d-flex gap-2">
            {admin.enabled && snapshot?._id ? (
              <Button size="sm" variant="outline-danger" onClick={() => admin.ask('one', [snapshot._id], ['Submission'])}>Delete</Button>
            ) : null}
            <Button size="sm" variant="outline-primary" onClick={load} disabled={loading}>
              Refresh
            </Button>
          </div>
        </div>
        {admin.modal}

        <div className="tsub__grid">
          <section className="tsub__panel">
            <header className="tsub__panel-head">
              <h4>Pinned versions</h4>
            </header>
            <div className="tsub__pins">
              <div>
                <strong>Credentials</strong>
                <ul>
                  {(snapshot.pinnedCredentials || []).map((c, i) => (
                    <li key={`c-${i}`}>{c.title || 'Credential'} · v{c.version || '—'}</li>
                  ))}
                  {!snapshot.pinnedCredentials?.length ? <li className="muted">None pinned</li> : null}
                </ul>
              </div>
              <div>
                <strong>CVs</strong>
                <ul>
                  {(snapshot.pinnedCvs || []).map((c, i) => (
                    <li key={`cv-${i}`}>{c.name || 'Person'} · v{c.cvVersion || '—'}</li>
                  ))}
                  {!snapshot.pinnedCvs?.length ? <li className="muted">None pinned</li> : null}
                </ul>
              </div>
              <div>
                <strong>Documents</strong>
                <ul>
                  {(snapshot.pinnedDocuments || []).map((d, i) => (
                    <li key={`d-${i}`}>{d.name || 'Document'} · v{d.versionLabel || '—'}</li>
                  ))}
                  {!snapshot.pinnedDocuments?.length ? <li className="muted">None pinned</li> : null}
                </ul>
              </div>
            </div>
            {snapshot.acknowledgementEvidence ? (
              <p className="tsub__evidence">{snapshot.acknowledgementEvidence}</p>
            ) : null}
          </section>

          <section className="tsub__panel">
            <header className="tsub__panel-head">
              <h4>Checklist at submit</h4>
              <span>{checklistDone}/{checklist.length}</span>
            </header>
            <ul className="tsub__checks tsub__checks--readonly">
              {checklist.map((row, i) => (
                <li key={i} className={row.completed ? 'is-done' : ''}>
                  {row.item}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section className="tsub__panel tsub__clarifications">
          <header className="tsub__panel-head">
            <h4>Clarifications</h4>
          </header>
          {(snapshot.clarifications || []).length ? (
            <Table size="sm" className="tsub__table mb-3">
              <thead>
                <tr>
                  <th>Asked</th>
                  <th>Question</th>
                  <th>Response</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {(snapshot.clarifications || []).map((c, index) => (
                  <tr key={index}>
                    <td>{fmtDate(c.requestedAt)}</td>
                    <td>{c.question}</td>
                    <td>
                      {c.respondedAt ? (
                        c.response || '—'
                      ) : (
                        <div className="tsub__respond">
                          <Form.Control
                            size="sm"
                            as="textarea"
                            rows={2}
                            placeholder="Response"
                            value={responseDrafts[index]?.response || ''}
                            onChange={(e) =>
                              setResponseDrafts((prev) => ({
                                ...prev,
                                [index]: { ...prev[index], response: e.target.value }
                              }))
                            }
                          />
                          <Form.Control
                            size="sm"
                            className="mt-1"
                            placeholder="Outcome"
                            value={responseDrafts[index]?.outcome || ''}
                            onChange={(e) =>
                              setResponseDrafts((prev) => ({
                                ...prev,
                                [index]: { ...prev[index], outcome: e.target.value }
                              }))
                            }
                          />
                          <Button
                            size="sm"
                            className="mt-1"
                            disabled={saving || !(responseDrafts[index]?.response || '').trim()}
                            onClick={() => respondClarification(index)}
                          >
                            Save response
                          </Button>
                        </div>
                      )}
                    </td>
                    <td>{c.outcome || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="tsub__empty">No clarifications logged yet.</p>
          )}
          <div className="tsub__add-clarification">
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Client clarification question"
              value={clarificationQ}
              onChange={(e) => setClarificationQ(e.target.value)}
            />
            <Button
              size="sm"
              disabled={saving || !clarificationQ.trim()}
              onClick={addClarification}
            >
              Log clarification
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="tsub">
      {error ? <Alert variant="warning">{error}</Alert> : null}

      <div className="tsub__banner tsub__banner--prep">
        <div>
          <h3>Submission desk</h3>
          <p>
            Pin versions, complete the pack checklist, capture the portal reference, then lock the
            immutable submission snapshot.
          </p>
        </div>
        <Button size="sm" variant="outline-primary" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </div>

      {blockers.length ? (
        <Alert variant="light" className="tsub__blockers">
          <strong>Required before a submission can be recorded</strong>
          <ul>
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </Alert>
      ) : (
        <Alert variant="light" className="small">
          The commercial estimate, an approved technical section, and an authorised Go or Conditional go are in place. Portal reference, acknowledgement, the Ready to submit stage, and bid-decision approval are still checked when the submission is recorded.
        </Alert>
      )}

      <Alert variant="light" className="small">
        <strong>Not a submission gate.</strong> Pinned credentials, pinned CVs, listed documents, and the pack-review reminder can be filled in. A tick there is not an approval.
        {' '}
        <strong>Not in this release:</strong> a generated submission pack, project finance, CV ranking, and technical or QCBS scoring. Delivery-team approval is recorded on Team / Resources and does not by itself approve this pack. Fee and EMD are checked separately. Finishing the Finance work package does not pass that check.
      </Alert>

      <div className="tsub__kpis">
        <div className="tsub__kpi">
          <span>Commercial</span>
          <strong>
            {readiness?.approvedEstimate
              ? (readiness.approvedEstimate.indicativePricePaise == null
                ? `Approved v${readiness.approvedEstimate.version}`
                : `v${readiness.approvedEstimate.version} · ${inrPaise(
                  readiness.approvedEstimate.indicativePricePaise,
                  readiness.approvedEstimate.currency
                )}`)
              : 'Not approved'}
          </strong>
        </div>
        <div className="tsub__kpi">
          <span>Technical</span>
          <strong>
            {readiness?.technical?.approvedCount || 0}/{readiness?.technical?.sectionCount || 0} approved
          </strong>
        </div>
        <div className="tsub__kpi">
          <span>Go / no-go</span>
          <strong>{labelize(readiness?.decision?.humanDecision)}</strong>
        </div>
        <div className="tsub__kpi">
          <span>Pins ready</span>
          <strong>
            {(readiness?.credentials?.length || 0) +
              (readiness?.personnel?.length || 0) +
              (readiness?.documents?.length || 0)}
          </strong>
        </div>
      </div>

      <div className="tsub__grid">
        <section className="tsub__panel">
          <header className="tsub__panel-head">
            <h4>Versions to pin</h4>
          </header>
          <div className="tsub__pin-tables">
            <div>
              <h5>Credentials ({readiness?.credentials?.length || 0})</h5>
              <Table size="sm" className="tsub__table mb-2">
                <thead>
                  <tr><th>Title</th><th>Version</th></tr>
                </thead>
                <tbody>
                  {(readiness?.credentials || []).map((c) => (
                    <tr key={String(c.credentialId)}>
                      <td>{c.title}{c.versionStale ? ' · library newer' : !c.versionPinned ? ' · needs pin' : ''}</td>
                      <td>v{c.version}</td>
                    </tr>
                  ))}
                  {!readiness?.credentials?.length ? (
                    <tr><td colSpan={2} className="muted">Attach credentials on this tender first</td></tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
            <div>
              <h5>Personnel CVs ({readiness?.personnel?.length || 0})</h5>
              <Table size="sm" className="tsub__table mb-2">
                <thead>
                  <tr><th>Name</th><th>CV</th></tr>
                </thead>
                <tbody>
                  {(readiness?.personnel || []).map((p) => (
                    <tr key={String(p.personnelId)}>
                      <td>
                        {p.name}{p.designation ? ` · ${p.designation}` : ''}
                        {p.versionStale ? ' · roster newer' : !p.versionPinned ? ' · needs pin' : ''}
                      </td>
                      <td>v{p.cvVersion}</td>
                    </tr>
                  ))}
                  {!readiness?.personnel?.length ? (
                    <tr><td colSpan={2} className="muted">Name people on this tender first</td></tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
            <div>
              <h5>Documents ({readiness?.documents?.length || 0})</h5>
              <Table size="sm" className="tsub__table mb-0">
                <thead>
                  <tr><th>File</th><th>Version</th></tr>
                </thead>
                <tbody>
                  {(readiness?.documents || []).map((d) => (
                    <tr key={String(d.documentId)}>
                      <td>{d.name}</td>
                      <td>v{d.versionLabel}</td>
                    </tr>
                  ))}
                  {!readiness?.documents?.length ? (
                    <tr><td colSpan={2} className="muted">Upload documents on the Documents tab</td></tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          </div>
        </section>

        <section className="tsub__panel">
          <header className="tsub__panel-head">
            <h4>Pack checklist</h4>
            <span>{checklistDone}/{checklist.length}</span>
          </header>
          <ul className="tsub__checks">
            {checklist.map((row, index) => (
              <li key={row.key || index}>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(row.completed)}
                    disabled={!editable || row.key === 'technical' || row.key === 'commercial'}
                    onChange={() => toggleCheck(index)}
                  />
                  <span>{row.item}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="tsub__panel tsub__record">
        <header className="tsub__panel-head">
          <h4>Portal & lock</h4>
        </header>
        <div className="tsub__fields">
          <Form.Group>
            <Form.Label>Portal reference</Form.Label>
            <Form.Control
              value={portalReference}
              disabled={!editable}
              onChange={(e) => setPortalReference(e.target.value)}
              placeholder="e.g. eProcurement bid ID / acknowledgement no."
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Acknowledgement evidence</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={acknowledgementEvidence}
              disabled={!editable}
              onChange={(e) => setAcknowledgementEvidence(e.target.value)}
              placeholder="Where the acknowledgement is filed (folder, email, portal receipt)"
            />
          </Form.Group>
        </div>
        {editable ? (
          <div className="tsub__actions">
            {canApproveSubmission ? (
              <Button size="sm" variant="outline-secondary" disabled={saving} onClick={async () => {
                setSaving(true);
                setError('');
                try {
                  await parivartanAPI.approveSubmissionPack(tenderId);
                  await load();
                } catch (err) {
                  setError(err?.response?.data?.message || 'The pack could not be approved.');
                } finally {
                  setSaving(false);
                }
              }}
              >
                Approve this submission pack
              </Button>
            ) : null}
            <Button
              size="sm"
              disabled={
                saving
                || !approval?.recordingAllowed
                || !readyToSubmit
                || !readiness?.canSubmit
                || !portalReference.trim()
                || !acknowledgementEvidence.trim()
              }
              onClick={record}
            >
              {saving ? 'Recording…' : 'Record portal submission'}
            </Button>
            <span className="tsub__hint">
              {isPreparer ? 'The tender owner prepares this pack and records the portal submission. Someone else, with bid-decision approval, approves the version. ' : ''}
              {approval?.recordingReason || 'The tender owner records the portal submission after an independent management approval.'}
              {' '}
              Management approval binds the reviewed versions. A later change makes it stale. Recording the acknowledgement is not pack approval. The approver cannot record the portal submission.
              {' '}
              The portal reference is the evidence that the bid was sent. Finishing the four work packages, or the Finance package, does not grant this approval.
            </span>
          </div>
        ) : (
          <p className="tsub__hint">This pursuit is locked for editing.</p>
        )}
      </section>
    </div>
  );
};

export default TenderSubmissionTab;
